import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  User, EnvelopeSimple, Phone, MapTrifold, Buildings,
  ShieldCheck, PencilSimple, Lock, Check, X
} from '@phosphor-icons/react';

export default function ProfilePage() {
  const { user, apiCall } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', address: '', mobile_no: '', work_no: '' });
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState({ type: '', text: '' });

  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  useEffect(() => { loadProfile(); }, []);

  async function loadProfile() {
    try {
      const res = await apiCall('get', '/auth/profile');
      setProfile(res.data);
      setForm({
        name: res.data.name || '',
        email: res.data.email || '',
        address: res.data.address || '',
        mobile_no: res.data.mobile_no || '',
        work_no: res.data.work_no || '',
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }

  async function handleSave() {
    setSaving(true);
    setSaveMsg({ type: '', text: '' });
    try {
      const res = await apiCall('put', '/auth/profile', form);
      setProfile(res.data);
      setEditing(false);
      setSaveMsg({ type: 'success', text: 'Profile updated successfully' });
      setTimeout(() => setSaveMsg({ type: '', text: '' }), 3000);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setSaveMsg({ type: 'error', text: typeof detail === 'string' ? detail : 'Failed to update profile' });
    } finally { setSaving(false); }
  }

  function cancelEdit() {
    setEditing(false);
    setForm({
      name: profile?.name || '',
      email: profile?.email || '',
      address: profile?.address || '',
      mobile_no: profile?.mobile_no || '',
      work_no: profile?.work_no || '',
    });
    setSaveMsg({ type: '', text: '' });
  }

  async function handlePasswordChange() {
    setPasswordError('');
    setPasswordSuccess('');
    if (!passwordForm.current || !passwordForm.newPass) {
      setPasswordError('All fields are required');
      return;
    }
    if (passwordForm.newPass !== passwordForm.confirm) {
      setPasswordError('New passwords do not match');
      return;
    }
    if (passwordForm.newPass.length < 6) {
      setPasswordError('New password must be at least 6 characters');
      return;
    }
    setPasswordLoading(true);
    try {
      await apiCall('post', '/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.newPass,
      });
      setPasswordSuccess('Password changed successfully');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
      setTimeout(() => { setShowPasswordDialog(false); setPasswordSuccess(''); }, 1500);
    } catch (e) {
      const detail = e.response?.data?.detail;
      setPasswordError(typeof detail === 'string' ? detail : 'Failed to change password');
    } finally { setPasswordLoading(false); }
  }

  const roleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Admin';
      case 'tenant_admin': return 'Tenant';
      default: return 'User';
    }
  };

  const roleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-primary/10 text-primary border-primary/20';
      case 'tenant_admin': return 'bg-warning/10 text-warning border-warning/20';
      default: return 'bg-info/10 text-info border-info/20';
    }
  };

  const initials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 animate-fadeIn max-w-4xl" data-testid="profile-page">
      {/* Header */}
      <div>
        <h4 className="text-xl font-semibold" data-testid="profile-heading">My Profile</h4>
        <p className="text-muted-foreground text-sm mt-0.5">Manage your account information</p>
      </div>

      {/* Alert Messages */}
      {saveMsg.text && (
        <div className={`p-3 text-sm rounded-md ${
          saveMsg.type === 'success' ? 'bg-success/10 border border-success/20 text-success' : 'bg-destructive/10 border border-destructive/20 text-destructive'
        }`} data-testid="profile-alert">
          {saveMsg.text}
        </div>
      )}

      {/* Profile Card */}
      <Card className="shadow-card rounded-lg border-0" data-testid="profile-card">
        <CardContent className="p-0">
          {/* Banner + Avatar */}
          <div className="h-28 bg-gradient-to-r from-primary/80 to-primary rounded-t-lg relative">
            <div className="absolute -bottom-10 left-6 flex items-end gap-4">
              <div className="w-[84px] h-[84px] rounded-lg bg-card border-4 border-card shadow-card flex items-center justify-center text-primary text-2xl font-bold">
                {initials(profile?.name)}
              </div>
              <div className="pb-2">
                <h5 className="text-lg font-semibold text-white drop-shadow-sm">{profile?.name || 'User'}</h5>
                <Badge className={`text-[11px] font-medium border ${roleBadgeColor(profile?.role)}`}>
                  <ShieldCheck size={12} className="mr-1" weight="bold" />
                  {roleLabel(profile?.role)}
                </Badge>
              </div>
            </div>
            {/* Edit / Change Password buttons */}
            <div className="absolute top-4 right-4 flex gap-2">
              {!editing ? (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => setEditing(true)}
                  className="rounded-md h-8 gap-1.5 text-xs font-medium bg-white/20 text-white border-0 hover:bg-white/30 backdrop-blur-sm"
                  data-testid="edit-profile-btn"
                >
                  <PencilSimple size={14} weight="bold" /> Edit
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    onClick={handleSave}
                    disabled={saving}
                    className="rounded-md h-8 gap-1.5 text-xs font-medium bg-white text-primary hover:bg-white/90"
                    data-testid="save-profile-btn"
                  >
                    <Check size={14} weight="bold" /> {saving ? 'Saving...' : 'Save'}
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    onClick={cancelEdit}
                    className="rounded-md h-8 gap-1.5 text-xs font-medium bg-white/20 text-white border-0 hover:bg-white/30 backdrop-blur-sm"
                    data-testid="cancel-edit-btn"
                  >
                    <X size={14} weight="bold" /> Cancel
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Profile Details */}
          <div className="pt-14 pb-6 px-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {/* Name */}
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium text-muted-foreground flex items-center gap-1.5">
                  <User size={14} /> Full Name
                </Label>
                {editing ? (
                  <Input
                    value={form.name}
                    onChange={(e) => setForm(p => ({ ...p, name: e.target.value }))}
                    className="rounded-md h-[38px]"
                    data-testid="profile-name-input"
                  />
                ) : (
                  <p className="text-sm font-medium h-[38px] flex items-center" data-testid="profile-name-value">{profile?.name || '—'}</p>
                )}
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium text-muted-foreground flex items-center gap-1.5">
                  <EnvelopeSimple size={14} /> Email
                </Label>
                {editing ? (
                  <Input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm(p => ({ ...p, email: e.target.value }))}
                    className="rounded-md h-[38px]"
                    data-testid="profile-email-input"
                  />
                ) : (
                  <p className="text-sm font-medium h-[38px] flex items-center" data-testid="profile-email-value">{profile?.email || '—'}</p>
                )}
              </div>

              {/* Mobile */}
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium text-muted-foreground flex items-center gap-1.5">
                  <Phone size={14} /> Mobile No.
                </Label>
                {editing ? (
                  <Input
                    value={form.mobile_no}
                    onChange={(e) => setForm(p => ({ ...p, mobile_no: e.target.value }))}
                    className="rounded-md h-[38px]"
                    data-testid="profile-mobile-input"
                  />
                ) : (
                  <p className="text-sm font-medium h-[38px] flex items-center" data-testid="profile-mobile-value">{profile?.mobile_no || '—'}</p>
                )}
              </div>

              {/* Work No */}
              <div className="space-y-1.5">
                <Label className="text-[13px] font-medium text-muted-foreground flex items-center gap-1.5">
                  <Phone size={14} /> Work No.
                </Label>
                {editing ? (
                  <Input
                    value={form.work_no}
                    onChange={(e) => setForm(p => ({ ...p, work_no: e.target.value }))}
                    className="rounded-md h-[38px]"
                    data-testid="profile-work-input"
                  />
                ) : (
                  <p className="text-sm font-medium h-[38px] flex items-center" data-testid="profile-work-value">{profile?.work_no || '—'}</p>
                )}
              </div>

              {/* Address */}
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-[13px] font-medium text-muted-foreground flex items-center gap-1.5">
                  <MapTrifold size={14} /> Address
                </Label>
                {editing ? (
                  <Input
                    value={form.address}
                    onChange={(e) => setForm(p => ({ ...p, address: e.target.value }))}
                    placeholder="Your address"
                    className="rounded-md h-[38px]"
                    data-testid="profile-address-input"
                  />
                ) : (
                  <p className="text-sm font-medium h-[38px] flex items-center" data-testid="profile-address-value">{profile?.address || '—'}</p>
                )}
              </div>
            </div>

            {/* Organization (read-only) */}
            {profile?.organization && (
              <div className="mt-5 pt-5 border-t border-border">
                <Label className="text-[13px] font-medium text-muted-foreground flex items-center gap-1.5 mb-1.5">
                  <Buildings size={14} /> Organization
                </Label>
                <p className="text-sm font-medium">{profile.organization}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Security Card */}
      <Card className="shadow-card rounded-lg border-0" data-testid="security-card">
        <CardContent className="p-6">
          <h5 className="text-base font-semibold mb-1">Security</h5>
          <p className="text-sm text-muted-foreground mb-4">Manage your password and account security</p>
          <Separator className="mb-4" />
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center">
                <Lock size={20} className="text-primary" weight="duotone" />
              </div>
              <div>
                <p className="text-sm font-medium">Password</p>
                <p className="text-xs text-muted-foreground">Last changed: Unknown</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => { setShowPasswordDialog(true); setPasswordError(''); setPasswordSuccess(''); setPasswordForm({ current: '', newPass: '', confirm: '' }); }}
              className="rounded-md text-xs font-medium"
              data-testid="change-password-btn"
            >
              Change Password
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info Card */}
      <Card className="shadow-card rounded-lg border-0" data-testid="account-card">
        <CardContent className="p-6">
          <h5 className="text-base font-semibold mb-1">Account Information</h5>
          <p className="text-sm text-muted-foreground mb-4">Your account details and membership</p>
          <Separator className="mb-4" />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
            <div>
              <p className="text-muted-foreground text-xs mb-1">Account ID</p>
              <p className="font-mono text-xs truncate">{profile?.id || '—'}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Role</p>
              <p className="font-medium capitalize">{(profile?.role || '').replace(/_/g, ' ')}</p>
            </div>
            <div>
              <p className="text-muted-foreground text-xs mb-1">Joined</p>
              <p className="font-medium">{profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-GB') : '—'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Change Password Dialog */}
      <Dialog open={showPasswordDialog} onOpenChange={setShowPasswordDialog}>
        <DialogContent className="rounded-lg" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-semibold">Change Password</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {passwordError && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 text-destructive text-sm rounded-md">{passwordError}</div>
            )}
            {passwordSuccess && (
              <div className="p-3 bg-success/10 border border-success/20 text-success text-sm rounded-md">{passwordSuccess}</div>
            )}
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Current Password</Label>
              <Input type="password" value={passwordForm.current} onChange={(e) => setPasswordForm(p => ({ ...p, current: e.target.value }))} placeholder="Enter current password" className="rounded-md h-[38px]" data-testid="current-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">New Password</Label>
              <Input type="password" value={passwordForm.newPass} onChange={(e) => setPasswordForm(p => ({ ...p, newPass: e.target.value }))} placeholder="Enter new password" className="rounded-md h-[38px]" data-testid="new-password-input" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[13px] font-medium">Confirm New Password</Label>
              <Input type="password" value={passwordForm.confirm} onChange={(e) => setPasswordForm(p => ({ ...p, confirm: e.target.value }))} placeholder="Confirm new password" className="rounded-md h-[38px]" data-testid="confirm-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button onClick={handlePasswordChange} disabled={passwordLoading} className="rounded-md" data-testid="save-password-btn">
              {passwordLoading ? 'Saving...' : 'Change Password'}
            </Button>
            <Button variant="outline" onClick={() => setShowPasswordDialog(false)} className="rounded-md">Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
